import { ANNOTATION_DATA_TYPE_INT } from '../../../../../constants/index.js';
import type {
  AnnotationDataEntry,
  AnnotationSchema,
  AnnotationSet,
  DataFile,
} from '../../../../../services/dataHubService.interface.js';
import type { DataHubService } from '../../../../../services/dataHubService.js';
import type { UsageContextOverrides } from '../../../../../services/usageContext.js';
import type {
  AnnotationPersistenceDef,
  SavedAnnotation,
} from '../annotationTypes/annotationType.interface.js';
import { EXPECTED_ACTION_PERSISTENCE } from '../annotationTypes/expectedActionPersistence.js';
import type { LabeledBy } from '../expectedAction/expectedActionPayload.js';

import {
  ANNOTATION_DRAFT_STATUS,
  ANNOTATION_EDITOR_PROPERTY,
  ANNOTATION_METADATA_VERSION,
  assertTargetFile,
  discoverDocuments,
  readDocumentMetadata,
} from './annotationDocument.js';
import type { AnnotationTarget } from './annotationDocument.js';

export { EXPECTED_ACTION_SCHEMA_NAME } from '../annotationTypes/expectedActionPersistence.js';
export const LOOKUP_PAGE_SIZE = 100;
const MAX_LOOKUP_PAGES = 50;

export type AnnotationService = Pick<
  DataHubService,
  | 'listAnnotationSchemas'
  | 'createAnnotationSchema'
  | 'listAnnotationSets'
  | 'createAnnotationSet'
  | 'createAnnotationSetDraft'
  | 'getAnnotationSetVersion'
  | 'annotationReadSourceItems'
  | 'listAnnotationData'
  | 'createAnnotationData'
  | 'annotationCreateUploadUrl'
  | 'uploadFileToBlob'
  | 'annotationCompleteUpload'
  | 'annotationDeleteFile'
  | 'annotationBatchGetDownloadUrls'
  | 'readTextFileFromBlob'
  | 'publishAnnotationSet'
>;

export class AnnotationConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AnnotationConflictError';
  }
}

/** A completed replacement can be durable while its predecessor still needs cleanup. */
export class AnnotationSaveRecoveryError extends Error {
  constructor(
    message: string,
    readonly target: AnnotationTarget,
    readonly uploadedFileId: string,
    readonly operationId: string,
  ) {
    super(message);
    this.name = 'AnnotationSaveRecoveryError';
  }
}

export async function allPages<T>(
  fetchPage: (
    continuationToken?: string,
  ) => Promise<{ items: T[]; continuationToken: string | null }>,
): Promise<T[]> {
  const items: T[] = [];
  let token: string | undefined;
  for (let page = 0; page < MAX_LOOKUP_PAGES; page++) {
    const result = await fetchPage(token);
    items.push(...result.items);
    if (!result.continuationToken) return items;
    token = result.continuationToken;
  }
  throw new Error(
    'Annotation discovery exceeded its page limit. Narrow the selection.',
  );
}

export async function resolveExpectedActionSchema(
  service: AnnotationService,
  persistence: AnnotationPersistenceDef = EXPECTED_ACTION_PERSISTENCE,
): Promise<AnnotationSchema> {
  const schemas = await allPages((continuationToken) =>
    service.listAnnotationSchemas({
      nameContains: persistence.schemaName,
      pageSize: LOOKUP_PAGE_SIZE,
      continuationToken,
    }),
  );
  const matches = schemas.filter(
    (schema) => schema.name === persistence.schemaName,
  );
  if (matches.length > 1)
    throw new Error(
      'Several schemas have this name. Select a schema explicitly.',
    );
  return (
    matches[0] ??
    service.createAnnotationSchema({
      name: persistence.schemaName,
      description: persistence.schemaDescription,
      schema: persistence.schemaDefinition,
    })
  );
}

export async function requireDraft(
  service: AnnotationService,
  target: Pick<
    AnnotationTarget,
    'dataSetId' | 'annotationSetId' | 'draftVersion'
  >,
): Promise<AnnotationSet> {
  const draft = await service.getAnnotationSetVersion(
    target.dataSetId,
    target.annotationSetId,
    'draft',
  );
  if (
    draft.status !== ANNOTATION_DRAFT_STATUS ||
    draft.version !== target.draftVersion
  ) {
    throw new AnnotationConflictError(
      'The selected annotation Draft changed or was published. Return to setup.',
    );
  }
  return draft;
}

export async function readTargetItem(
  service: AnnotationService,
  target: AnnotationTarget,
): Promise<AnnotationDataEntry> {
  await requireDraft(service, target);
  const items = await service.annotationReadSourceItems(
    target.dataSetId,
    target.annotationSetId,
    target.draftVersion,
    [target.sourceDataId],
  );
  const item = items.find(
    (entry) =>
      entry.dataId === target.annotationDataId &&
      entry.rawDataId === target.sourceDataId,
  );
  if (item === undefined)
    throw new Error(
      'The selected annotation item no longer exists in this Draft.',
    );
  return item;
}

export async function ensureAnnotationItem(
  service: AnnotationService,
  dataSetId: string,
  draft: AnnotationSet,
  rawDataId: string,
  name: string,
  usage?: UsageContextOverrides,
): Promise<AnnotationDataEntry> {
  await requireDraft(service, {
    dataSetId,
    annotationSetId: draft.annotationSetId,
    draftVersion: draft.version,
  });
  const items = await service.annotationReadSourceItems(
    dataSetId,
    draft.annotationSetId,
    draft.version,
    [rawDataId],
  );
  if (items.length > 1)
    throw new AnnotationConflictError(
      'Multiple annotation items reference this source item.',
    );
  return (
    items[0] ??
    service.createAnnotationData(
      dataSetId,
      draft.annotationSetId,
      {
        dataType: ANNOTATION_DATA_TYPE_INT,
        name,
        rawDataId,
        category: EXPECTED_ACTION_PERSISTENCE.kind,
      },
      usage,
    )
  );
}

export interface SaveExpectedActionsInput {
  service: AnnotationService;
  target: AnnotationTarget;
  actions: SavedAnnotation[];
  sourceContentHash?: string;
  labeledBy?: LabeledBy;
  annotationType?: AnnotationPersistenceDef;
  usage?: UsageContextOverrides;
}

function selectedFile(
  target: AnnotationTarget,
  files: readonly DataFile[],
): DataFile | undefined {
  const heads = discoverDocuments(files);
  if (target.fileId === undefined) {
    if (
      files.some(
        (file) => readDocumentMetadata(file)?.documentId === target.documentId,
      )
    ) {
      throw new AnnotationConflictError(
        'This document was already created. Resume it from setup.',
      );
    }
    return undefined;
  }
  const file = heads.find((candidate) => candidate.id === target.fileId);
  if (file === undefined)
    throw new AnnotationConflictError(
      'This annotation document changed. Reload before saving.',
    );
  assertTargetFile(target, file);
  if (
    files.some(
      (candidate) =>
        readDocumentMetadata(candidate)?.documentId === target.documentId &&
        candidate.id !== file.id,
    )
  ) {
    throw new AnnotationConflictError(
      'This document has an unfinished replacement. Retry cleanup before editing.',
    );
  }
  return file;
}

async function finishReplacement(
  service: AnnotationService,
  target: AnnotationTarget,
  uploadedFileId: string,
  operationId: string,
  usage?: UsageContextOverrides,
): Promise<AnnotationTarget> {
  const item = await readTargetItem(service, target);
  const files = item.dataFiles ?? [];
  const replacement = discoverDocuments(files).find(
    (file) => file.id === uploadedFileId,
  );
  if (replacement === undefined)
    throw new Error(
      'The save could not be confirmed. Keep this session open and retry reconciliation.',
    );
  assertTargetFile(target, replacement);
  if (readDocumentMetadata(replacement)?.saveOperationId !== operationId)
    throw new AnnotationConflictError('The save identity changed.');
  if (target.fileId !== undefined && target.fileId !== uploadedFileId) {
    const predecessor = files.find((file) => file.id === target.fileId);
    if (predecessor !== undefined) {
      assertTargetFile(target, predecessor);
      if (
        readDocumentMetadata(replacement)?.supersedesFileId !== predecessor.id
      )
        throw new AnnotationConflictError(
          'The replacement does not match the selected predecessor.',
        );
      await service.annotationDeleteFile(
        target.dataSetId,
        target.annotationSetId,
        target.annotationDataId,
        predecessor.id,
        usage,
      );
    }
  }
  return { ...target, fileId: uploadedFileId, legacy: false };
}

export async function recoverAnnotationSave(
  service: AnnotationService,
  error: AnnotationSaveRecoveryError,
  usage?: UsageContextOverrides,
): Promise<AnnotationTarget> {
  return finishReplacement(
    service,
    error.target,
    error.uploadedFileId,
    error.operationId,
    usage,
  );
}

/** Create/replace only the selected attachment. Set/item creation and publication never occur here. */
export async function saveExpectedActions(
  input: SaveExpectedActionsInput,
): Promise<AnnotationTarget> {
  const { service, target } = input;
  const persistence = input.annotationType ?? EXPECTED_ACTION_PERSISTENCE;
  if (persistence.kind !== target.kind)
    throw new Error('The editor does not match the selected document schema.');
  const item = await readTargetItem(service, target);
  const previous = selectedFile(target, item.dataFiles ?? []);
  const operationId = crypto.randomUUID();
  const file = new File(
    [
      persistence.serialize({
        actions: input.actions,
        fileId: target.sourceFileId,
        sourceContentHash: input.sourceContentHash,
        labeledBy: input.labeledBy,
      }),
    ],
    target.fileName,
    { type: 'application/json' },
  );
  const upload = await service.annotationCreateUploadUrl(
    target.dataSetId,
    target.annotationSetId,
    target.annotationDataId,
    file.name,
    input.usage,
  );
  await service.uploadFileToBlob(upload.uploadUrl, file);
  // Recheck after upload; another client may have published or replaced this file meanwhile.
  selectedFile(target, (await readTargetItem(service, target)).dataFiles ?? []);
  try {
    await service.annotationCompleteUpload(
      target.dataSetId,
      target.annotationSetId,
      target.annotationDataId,
      {
        id: upload.fileId,
        fileName: file.name,
        fileType: 'generic',
        fileSizeInBytes: file.size,
        properties: {
          ...previous?.properties,
          [ANNOTATION_EDITOR_PROPERTY]: {
            formatVersion: ANNOTATION_METADATA_VERSION,
            documentId: target.documentId,
            kind: target.kind,
            schemaId: target.schemaId,
            sourceDataId: target.sourceDataId,
            sourceFileId: target.sourceFileId,
            sourceDatasetVersion: target.sourceDatasetVersion,
            sourceContentHash: input.sourceContentHash,
            saveOperationId: operationId,
            supersedesFileId: target.fileId,
          },
        },
      },
    );
    return await finishReplacement(service, target, upload.fileId, operationId);
  } catch (error) {
    throw new AnnotationSaveRecoveryError(
      `Save requires reconciliation or cleanup: ${error instanceof Error ? error.message : 'TEDH request failed'}`,
      target,
      upload.fileId,
      operationId,
    );
  }
}

export interface LoadExpectedActionsInput {
  service: AnnotationService;
  target: AnnotationTarget;
  annotationType?: AnnotationPersistenceDef;
}

export async function loadExpectedActions(
  input: LoadExpectedActionsInput,
): Promise<SavedAnnotation[]> {
  const { service, target } = input;
  if (target.fileId === undefined)
    throw new Error('Start a document explicitly before editing.');
  const item = await readTargetItem(service, target);
  const file = selectedFile(target, item.dataFiles ?? []);
  if (file === undefined)
    throw new Error('The selected annotation file is missing.');
  const result = await service.annotationBatchGetDownloadUrls(
    target.dataSetId,
    target.annotationSetId,
    String(target.draftVersion),
    [{ dataId: target.annotationDataId }],
    undefined,
    true,
  );
  const url = result.succeededItems.find(
    (entry) => entry.dataId === target.annotationDataId,
  )?.files[target.fileId];
  if (result.failedItems.length > 0 || url === undefined)
    throw new Error('The selected annotation file could not be downloaded.');
  const text = await service.readTextFileFromBlob(url);
  return (input.annotationType ?? EXPECTED_ACTION_PERSISTENCE).parse(
    text,
    target.sourceFileId,
  );
}

/** Shared by setup and the legacy manager. Never publishes a partially replaced managed document. */
export async function preflightAnnotationPublish(
  service: AnnotationService,
  dataSetId: string,
  annotationSetId: string,
  draftVersion: number,
): Promise<void> {
  await requireDraft(service, { dataSetId, annotationSetId, draftVersion });
  const items = await allPages((continuationToken) =>
    service.listAnnotationData({
      dataSetId,
      annotationSetId,
      pageSize: LOOKUP_PAGE_SIZE,
      continuationToken,
      includeCount: false,
    }),
  );
  for (const item of items) {
    const files = item.dataFiles ?? [];
    const heads = discoverDocuments(files);
    if (heads.length !== files.length)
      throw new AnnotationConflictError(
        'A saved document still needs predecessor cleanup. Resume it before publishing.',
      );
  }
  await requireDraft(service, { dataSetId, annotationSetId, draftVersion });
}

export interface ListAnnotatedFileIdsInput {
  service: AnnotationService;
  dataSetId: string;
  kinds?: readonly string[];
}

export async function listAnnotatedFileIds(
  input: ListAnnotatedFileIdsInput,
): Promise<Set<string>> {
  return new Set((await listAnnotationFileStates(input)).keys());
}

export async function listAnnotationFileStates(
  input: ListAnnotatedFileIdsInput,
): Promise<Map<string, Set<AnnotationSet['status']>>> {
  const kinds = input.kinds ?? [EXPECTED_ACTION_PERSISTENCE.kind];
  const sets = await allPages((continuationToken) =>
    input.service.listAnnotationSets({
      dataSetId: input.dataSetId,
      pageSize: LOOKUP_PAGE_SIZE,
      continuationToken,
      includeCount: false,
    }),
  );
  const states = new Map<string, Set<AnnotationSet['status']>>();
  const add = (sourceFileId: string, status: AnnotationSet['status']) => {
    const saved =
      states.get(sourceFileId) ?? new Set<AnnotationSet['status']>();
    saved.add(status);
    states.set(sourceFileId, saved);
  };
  for (const set of sets) {
    if (
      kinds.includes(String(set.properties?.kind)) &&
      typeof set.properties?.rawDataFileId === 'string'
    )
      add(set.properties.rawDataFileId, set.status);
    const items = await allPages((continuationToken) =>
      input.service.listAnnotationData({
        dataSetId: input.dataSetId,
        annotationSetId: set.annotationSetId,
        pageSize: LOOKUP_PAGE_SIZE,
        continuationToken,
        includeCount: false,
      }),
    );
    for (const item of items)
      for (const file of discoverDocuments(item.dataFiles ?? [])) {
        const metadata = readDocumentMetadata(file);
        if (metadata !== undefined && kinds.includes(metadata.kind))
          add(metadata.sourceFileId, set.status);
      }
  }
  return states;
}
